import React, { useRef } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';

import LikeAndShareCourse from '@components/share/custom/likeAndShareCourse';
import CourseHeaderPrice from '@components/templates/courseDetail/courseRegularBanner/header/purchaseDetail/coursePrice';
import CourseReview from '@components/templates/courseDetail/courseRegularBanner/header/purchaseDetail/courseReview';
import CourseRegularHeaderPurchaseDetailSkeleton from '@components/templates/courseDetail/courseRegularBanner/header/purchaseDetail/skeleton';
import { CourseHeaderBannerPurchaseDetailContainer } from '@components/templates/courseDetail/courseRegularBanner/header/purchaseDetail/style';
import CourseRegularBannerPurchaseButton from '@components/templates/courseDetail/courseRegularBanner/purchaseButton';
import { useBreakpointV2 } from '@helpers/use';
import useCourseRegularBanner from '@hooks/course/courseDetail/useCourseRegularBanner';
import useCourseDetail from '@hooks/course/useCourseDetail';
import { CourseEntity } from '@redux/course/type/courseDetailType';

const MINIMUM_COURSE_NAME_HEIGHT = 248;

type CourseHeaderBannerPurchaseDetail = {
  courseDetailSeo: CourseEntity;
};

const CourseHeaderBannerPurchaseDetail = ({ courseDetailSeo }: CourseHeaderBannerPurchaseDetail) => {
  const { t } = useTranslation('store_course');
  const { name } = courseDetailSeo;
  const { price, review, isCourseDetailLoading } = useCourseDetail();
  const { isDesktop } = useBreakpointV2();

  const { isEnrolled, courseDetailData } = useCourseRegularBanner();
  const theme = useTheme();
  const courseNameRef = useRef<HTMLElement>(null);

  const isDisableCoursePrice = isEnrolled;

  if (isCourseDetailLoading) {
    return (
      <CourseHeaderBannerPurchaseDetailContainer>
        <CourseRegularHeaderPurchaseDetailSkeleton />
      </CourseHeaderBannerPurchaseDetailContainer>
    );
  }

  const courseNameElementHeight = courseNameRef?.current?.clientHeight || 0;

  return (
    <CourseHeaderBannerPurchaseDetailContainer>
      <Box>
        <Typography variant="h5" color={theme.palette.textOnPrimary.secondary}>
          {t('course_detail.header_banner.online_course')}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '12px' }} ref={courseNameRef}>
          <Typography
            sx={{
              typography: {
                md: 'h1',
                xs: 'h4',
              },
              color: theme.palette.textOnPrimary.primary,
              lineHeight: {
                lg: '43px',
                md: '36px',
              },
            }}
          >
            {name}
          </Typography>
          {!isDesktop && <LikeAndShareCourse courseDetailSeo={courseDetailSeo} />}
        </Box>

        {isDisableCoursePrice || !price?.regular ? null : (
          <CourseHeaderPrice salePrice={price?.regular?.salePrice} fullPrice={price?.regular?.fullPrice} />
        )}

        {review ? (
          <CourseReview reviewAverageRating={review.reviewAverageRating} totalReview={review.totalReview} />
        ) : null}
      </Box>

      <Box>
        <CourseRegularBannerPurchaseButton
          isDisableActionIcon={!isDesktop}
          courseDetailData={courseDetailData}
          courseDetailSeo={courseDetailSeo}
        />
      </Box>
    </CourseHeaderBannerPurchaseDetailContainer>
  );
};

export default CourseHeaderBannerPurchaseDetail;
